import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';

interface QueryState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
  setData: (updater: T | ((prev: T | null) => T)) => void;
}

/**
 * Minimal data fetching. No cache and no library: almost every screen here reads one or
 * two endpoints on mount, and the live screens need explicit invalidation anyway, which is
 * what `reload` and the `deps` array give.
 */
export function useQuery<T>(path: string | null, deps: unknown[] = []): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [nonce, setNonce] = useState(0);
  // Guards against a slow response for a path the user has already navigated away from.
  const latest = useRef(0);

  useEffect(() => {
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    const ticket = ++latest.current;
    setLoading(true);
    setError(null);
    api
      .get<T>(path)
      .then((res) => {
        if (ticket === latest.current) setData(res);
      })
      .catch((err) => {
        if (ticket === latest.current) setError(err);
      })
      .finally(() => {
        if (ticket === latest.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const update = useCallback((updater: T | ((prev: T | null) => T)) => {
    setData((prev) => (typeof updater === 'function' ? (updater as (p: T | null) => T)(prev) : updater));
  }, []);

  return { data, error, loading, reload, setData: update };
}

/**
 * Wrap a mutation so a screen gets `pending` and a surfaced error without a try/catch.
 *
 * `run` is a stable identity — screens pass it straight to onClick — but it must call the
 * *latest* callback, not the one from the first render. Callers invariably close over
 * component state (`() => api.post('/auth/login', { email, password })`), so capturing the
 * function once would post whatever those fields held at mount: empty strings.
 * The ref is what keeps a stable `run` and fresh state from being mutually exclusive.
 */
export function useAction<Args extends unknown[], R>(fn: (...args: Args) => Promise<R>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const latest = useRef(fn);
  latest.current = fn;

  const run = useCallback(async (...args: Args): Promise<R | undefined> => {
    setPending(true);
    setError(null);
    try {
      return await latest.current(...args);
    } catch (err) {
      setError(err);
      return undefined;
    } finally {
      setPending(false);
    }
  }, []);

  return { run, pending, error, clearError: () => setError(null) };
}
